<template>
  <div>
    <base-wrapper class="space-y-6">
      <create-order-form
        :valid="order.valid"
        with-destination-area
        with-stockist-center-balance
        with-reference-code
        with-origin-stockist
        with-address-receipt
        :disabled-date="false"
        :use-custom-address="true"
        :use-backup-upline-address="true"
        v-model="order"
        @change-destination-office="onChangeDestinationOffice"
      />
      <create-order-detail-form
        v-if="canCreateOrderDetail"
        with-global-stock
        :columns="[
          'code',
          'name',
          'stock',
          'stock_free',
          'qty',
          'price',
          'total_price',
          'action',
        ]"
        :stock-free-use-global="false"
        :disabled="order.valid"
        :order-id="order.id"
        :origin-office="{ id: me.office_id, code: me.office_code }"
        :destination-office="{
          id: order.destinationOffice.id,
          code: order.destinationOffice.code,
        }"
        :min-spend="minOrderSpend"
        :origin-warehouse="{
          id: order.originWarehouse.id,
          code: order.originWarehouse.code,
        }"
        :buyer-type="{
          id: order.buyerType.id,
          code: order.buyerType.code,
        }"
        :area="{
          id: order.area.id,
          code: order.area.code,
        }"
        :create-order-attributes="{
          is_loan: order.loan,
          is_using_stock: order.using_stock,
          destination_warehouse_id: order.destinationOffice.currentWarehouse.id,
          order_type: 'sale',
          order_shipment: order.shipment_type,
          reference_code: order.referenceCode,
          orderedAt: order.date,
          ...(order.custom_shipment_address
            ? {
                receipt_name: order.shipmentAddress.receipt_name,
                receipt_phone: order.shipmentAddress.receipt_phone,
                destination_address: {
                  province_id: order.shipmentAddress.province.id,
                  city_id: order.shipmentAddress.city.id,
                  district_id: order.shipmentAddress.district.id,
                  village_id: order.shipmentAddress.village.id,
                  address: order.shipmentAddress.address,
                },
              }
            : null),
        }"
        v-model="orderDetails"
        @order-created="onOrderCreated"
        @order-detail-updated="onOrderDetailUpdated"
      />
      <template v-if="canCreatePayment">
        <order-promo-list
          v-if="order.using_stock && productBundles.length"
          :order-details="orderDetailsForPromoList"
          :product-bundles="productBundles"
          :stocks="productBundlesBonusStocks"
          :warehouse-stocks="productBundlesBonusWarehouseStocks"
          @change-selected-bundles="onChangeSelectedBundles"
          @change-choosen-bonuses="onChangeChoosenBonuses"
          @update-remainder-status="status => promoHasRemainder = status"
        />
          
        <create-order-summary
          payment-type="balance"
          :order-details="orderDetails"
          :payment-methods="balanceUsed"
          :with-cashback="false"
          :with-tax="false"
          v-model="order"
        />
        <create-balance-payment-form
          :disabled="order.valid"
          :payment-id="payment.id"
          v-model="balanceUsed"
          @update-restock-balance="onUpdateRestockBalance"
        />
      </template>
      <div class="flex justify-end gap-x-2">
        <base-button
          v-if="!order.valid"
          :disabled="!canValidate"
          :title="promoHasRemainder ? 'Ada promo yang bisa dipilih' : ''"
          @click="onValidate"
          >Validasi</base-button
        >
        <base-button :to="{ name: 'restock-sc' }" color="white"
          >Kembali</base-button
        >
      </div>
    </base-wrapper>
    <view-order-modal
      :visible="viewOrderModalVisible"
      :order-id="order.id"
      with-bonus
      @validated="$router.push({ name: 'restock-sc' })"
      @close="$router.push({ name: 'restock-sc' })"
    />
    <validate-order-confirmation
      :order-id="order.id"
      :visible="confirmValidateOrderVisible"
      :validation-data="{
        selected_bundles_id: this.selectedBundes,
        choosen_bonuses: this.choosenBonuses
      }"
      @close="confirmValidateOrderVisible = false"
      @confirmed="onValidateConfirmed"
    />
    <loading v-if="loading" />
  </div>
</template>

<script>
import CreateOrderForm from '@/components/order/create-order-form.vue';
import CreateOrderDetailForm from '@/components/order/create-order-detail-form.vue';
import CreateBalancePaymentForm from '@/components/payment/create-balance-payment-form.vue';
import { mapActions, mapGetters } from 'vuex';
import { requestMixin } from '@/mixins/request/request';
import ViewOrderModal from '@/components/order/view-order-modal.vue';
import ValidateOrderConfirmation from '@/components/order/validate-order-confirmation.vue';
import CreateOrderSummary from '@/components/order/create-order-summary.vue';
import OrderPromoList from '@/components/promo/OrderPromoList.vue';

export default {
  mixins: [requestMixin],
  components: {
    CreateOrderForm,
    CreateOrderDetailForm,
    CreateBalancePaymentForm,
    ViewOrderModal,
    ValidateOrderConfirmation,
    CreateOrderSummary,
    OrderPromoList
  },
  data() {
    return {
      balanceUsed: [
        {
          paymentMethodId: null,
          id: 'restock',
          type: 'restock_balance',
          name: 'Saldo Restock',
          balance: 0,
          amount: null,
          originalAmount: null,
          used: false,
          saved: false
        },
        {
          paymentMethodId: null,
          id: 'multiplied_deposit',
          type: 'multiplied_deposit_balance',
          name: 'Nilai Barang Dari Deposit',
          balance: 0,
          amount: null,
          originalAmount: null,
          used: false,
          saved: false
        },
      ],
      confirmValidateOrderVisible: false,
      loadingBuyerType: false,
      loadingOrder: false,
      loadingPayment: false,
      loadingSetup: false,
      minOrderSpend: 0,
      order: {
        id: null,
        referenceCode: null,
        referenceCodeSaved: false,
        shippingFee: 0,
        plasticQty: 0,
        area: {
          id: null,
          code: null,
        },
        buyerType: {
          id: null,
          code: null,
        },
        date: new Date(),
        destinationOfficeType: 'stockist_center',
        destinationOffice: {
          id: null,
          code: null,
          searchCode: null,
          originalCode: null,
          name: null,
          restock_balance: null,
          temp_restock_balance: null,
          multiplied_deposit_balance: null,
          hasAddress: false,
          address: null,
          uplineHistories: [],
          currentWarehouse: {
            id: null,
            code: null,
          },
          area: {
            id: null,
            code: null,
          },
        },
        loan: false,
        originWarehouse: {
          id: null,
          code: null,
          searchCode: null,
          originalCode: null,
          name: null,
          officeId: null,
          area: {
            id: null,
            code: null,
          },
        },
        shipment_type: 'pickup',
        shipmentAddress: {
          receipt_name: null,
          receipt_phone: null,
          province: {
            id: null,
            name: null,
          },
          city: {
            id: null,
            name: null,
          },
          district: {
            id: null,
            name: null,
          },
          village: {
            id: null,
            name: null,
          },
          address: null,
          area: {
            id: null,
            code: null,
          },
        },
        custom_shipment_address: false,
        using_stock: true,
        valid: false,
      },
      orderDetails: [
        {
          id: null,
          productId: null,
          productCode: null,
          originalProductCode: null,
          productName: null,
          stock: null,
          stock_free: null,
          qty: null,
          originalQty: null,
          price: null,
          totalPrice: null,
        },
      ],
      payment: {
        id: null,
      },
      productBundles: [],
      productBundlesBonusStocks: {},
      productBundlesBonusWarehouseStocks: {},
      selectedBundes: [],
      choosenBonuses: [],
      promoHasRemainder: true,
      viewOrderModalVisible: false,
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    canCreateOrderDetail() {
      return this.order.destinationOffice.id && this.order.originWarehouse.id;
    },
    canCreatePayment() {
      return (
        this.canCreateOrderDetail && this.order.using_stock && !this.order.loan && this.totalPrice > 0
      );
    },
    canValidate() {
      if (!this.order.using_stock) {
        return this.totalPrice > 0
      }

      if (this.productBundles.length > 0 && this.promoHasRemainder) {
        return false
      }

      return this.totalPayment === this.totalPrice;
    },
    loading() {
      return (
        this.loadingBuyerType ||
        this.loadingOrder ||
        this.loadingPayment ||
        this.loadingSetup
      );
    },
    totalPrice() {
      return this.orderDetails.reduce(
        (total, orderDetail) => total + orderDetail.totalPrice,
        0
      );
    },
    totalPayment() {
      return this.balanceUsed.reduce(
        (total, item) => total + (typeof item.amount === 'string' ? parseInt(item.amount.replace(/\D/gi, '')) : item.amount),
        0
      );
    },
    orderDetailsForPromoList() {
      return this.orderDetails
        .filter(item => item.productId)
        .map(item => ({
          productId: item.productId,
          qty: item.qty,
          productCode: item.productCode 
        }))
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async loadBuyerType() {
      this.loadingBuyerType = true;

      const [res, error] = await this.request('/api/v1/buyer-types', {
        params: {
          'filter[code]': 10,
          'fields[buyer-types]': 'code',
        },
      });

      if (!error) {
        const buyerType = res.data[0];

        this.order.buyerType.id = buyerType.id;
        this.order.buyerType.code = buyerType.attributes.code;
      }

      this.loadingBuyerType = false;
    },
    async loadOrder() {
      this.loadingOrder = true;

      this.order.id = this.$route.params.id;

      const [res, error] = await this.request(
        `/api/v1/orders/${this.order.id}`,
        {
          params: {
            include:
              'area,buyer-type,origin-office,destination-office,destination-warehouse,origin-warehouse,order-details',
            'fields[orders]':
              'reference_code,receipt_name,receipt_phone,orderedAt,shipping_fee,plastic_qty,createdAt,order_shipment,is_using_stock,is_loan,shipment_address,is_completed,is_valid_for_packing,area,buyer-type,destination-office,destination-warehouse,origin-warehouse,order-details,origin-office',
            'fields[areas]': 'code',
            'fields[buyer-types]': 'code',
            'fields[offices]':
              'code,name,area_code,restock_balance,temp_restock_balance,multiplied_deposit_balance,address,upline_histories',
            'fields[warehouses]': 'code,name,area_code',
            'fields[order-details]':
              'product_id,product_code,product_name,current_stock,current_stock_free,product_qty,product_price,total_price',
          },
        }
      );

      if (!error && res.data) {
        const order = res;

        const area = this.getSingleIncluded(
          res,
          order.data.relationships.area.data.id
        );
        const buyerType = this.getSingleIncluded(
          res,
          order.data.relationships['buyer-type'].data.id
        );
        const originOffice = this.getSingleIncluded(
          res,
          order.data.relationships['origin-office'].data.id
        );
        const destinationOffice = this.getSingleIncluded(
          res,
          order.data.relationships['destination-office'].data.id
        );
        const destinationWarehouse = this.getSingleIncluded(
          res,
          order.data.relationships['destination-warehouse'].data.id
        );
        const originWarehouse = this.getSingleIncluded(
          res,
          order.data.relationships['origin-warehouse'].data.id
        );
        const orderDetails = this.getIncludedByType(res, 'order-details');

        this.order.shippingFee = order.data.attributes.shipping_fee
        this.order.plasticQty = order.data.attributes.plastic_qty
        this.order.referenceCode = order.data.attributes.reference_code;
        this.order.referenceCodeSaved = true;

        this.order.shipmentAddress.receipt_name =
          order.data.attributes.receipt_name;
        this.order.shipmentAddress.receipt_phone =
          order.data.attributes.receipt_phone;

        this.order.buyerType.id = buyerType.id;
        this.order.buyerType.code = buyerType.attributes.code;

        this.order.date = order.data.attributes.orderedAt;
        this.order.destinationOffice.id = destinationOffice.id

        this.order.destinationOffice.code = destinationOffice.attributes.code;
        this.order.destinationOffice.searchCode = `${destinationOffice.attributes.code} (${destinationOffice.attributes.area_code})`;
        this.order.destinationOffice.originalCode =
          this.order.destinationOffice.searchCode;
        this.order.destinationOffice.name = destinationOffice.attributes.name;
        this.order.destinationOffice.restock_balance =
          destinationOffice.attributes.restock_balance;
        this.order.destinationOffice.temp_restock_balance =
          destinationOffice.attributes.temp_restock_balance;
        this.order.destinationOffice.multiplied_deposit_balance =
          destinationOffice.attributes.multiplied_deposit_balance;

        this.order.destinationOffice.currentWarehouse.id =
          destinationWarehouse.id;
        this.order.destinationOffice.currentWarehouse.code =
          destinationWarehouse.attributes.code;

        this.order.destinationOffice.area.code =
          destinationOffice.attributes.area_code;

        this.order.destinationOffice.uplineHistories = destinationOffice.attributes.upline_histories

        this.order.originWarehouse.id = originWarehouse.id;
        this.order.originWarehouse.code = originWarehouse.attributes.code;
        this.order.originWarehouse.searchCode = `${originWarehouse.attributes.code} (${originWarehouse.attributes.area_code})`;
        this.order.originWarehouse.originalCode =
          this.order.originWarehouse.searchCode;
        this.order.originWarehouse.name = originWarehouse.attributes.name;
        this.order.originWarehouse.officeId = originOffice.id;

        this.order.originWarehouse.area.code =
          originWarehouse.attributes.area_code;

        this.order.area.id = area.id;
        this.order.area.code = area.attributes.code;

        this.order.shipment_type = order.data.attributes.order_shipment;
        this.order.using_stock = order.data.attributes.is_using_stock;
        this.order.loan = order.data.attributes.is_loan;

        this.order.shipmentAddress.address =
          order.data.attributes.shipment_address ||
          destinationOffice.attributes.address;

        this.order.valid =
          order.data.attributes.is_completed ||
          order.data.attributes.is_valid_for_packing;

        this.orderDetails = orderDetails.map((orderDetail) => ({
          id: orderDetail.id,
          productId: orderDetail.attributes.product_id,
          productCode: orderDetail.attributes.product_code,
          originalProductCode: orderDetail.attributes.product_code,
          productName: orderDetail.attributes.product_name,
          stock: orderDetail.attributes.current_stock,
          stock_free: orderDetail.attributes.current_stock_free,
          qty: orderDetail.attributes.product_qty || null,
          originalQty: orderDetail.attributes.product_qty || null,
          price: orderDetail.attributes.product_price,
          totalPrice: orderDetail.attributes.total_price,
        }));

        if (!this.order.valid) {
          this.orderDetails.push({
            id: null,
            productId: null,
            productCode: null,
            originalProductCode: null,
            productName: null,
            stock: null,
            stock_free: null,
            qty: null,
            originalQty: null,
            price: null,
            totalPrice: null,
          });
        }

        if (
          order.data.attributes.is_using_stock &&
          !order.data.attributes.is_loan
        ) {
          this.loadPayment();
        }

        this.updateBalanceUsedBalance();
        this.loadProductBundles()
      }

      this.loadingOrder = false;
    },
    async loadPayment() {
      this.loadingPayment = true;

      const [res, err] = await this.request(
        `/api/v1/orders/${this.order.id}/payments`,
        {
          params: {
            include: 'payment-methods',
            'fields[payments]': 'payment-methods',
            'fields[payment-methods]': 'payment_amount,balance_used',
          },
        }
      );

      if (!err) {
        this.payment.id = res.data[0].id;

        const paymentMethods = this.getIncludedByType(res, 'payment-methods');

        this.balanceUsed.forEach((balanceUsed, index) => {
          const paymentMethod = paymentMethods.find(
            (paymentMethod) =>
              paymentMethod.attributes.balance_used === balanceUsed.id
          );

          if (paymentMethod) {
            this.balanceUsed[index].used = true;
            this.balanceUsed[index].saved = true;
            this.balanceUsed[index].paymentMethodId = paymentMethod.id;
            this.balanceUsed[index].amount =
              paymentMethod.attributes.payment_amount;
            this.balanceUsed[index].originalAmount =
              paymentMethod.attributes.payment_amount;
          }
        });
      }

      this.loadingPayment = false;
    },
    async loadProductBundles() {
      const [res, err] = await this.request(`/api/v1/orders/${this.order.id}/product-bundles`, {
        params: {
          sort: 'sort_order'
        }
      })

      if (!err) {
        this.productBundles = res.data
        this.productBundlesBonusStocks = res.meta.free_stocks
        this.productBundlesBonusWarehouseStocks = res.meta.warehouse_free_stocks
      }
    },
    async loadSetup() {
      this.loadingSetup = true;

      const [res, err] = await this.request('/api/v1/setups', {
        params: {
          'filter[setup_key]': 'min_order_spend_for_sc',
          'fields[setups]': 'setup_value',
        },
      });

      if (!err && res.data.length) {
        this.minOrderSpend = +res.data[0].attributes.setup_value;
      }

      this.loadingSetup = false;
    },
    onChangeDestinationOffice() {
      this.updateBalanceUsedBalance();
    },
    onOrderCreated(order) {
      this.order.id = order.data.id;

      this.$router.push({
        name: 'restock-sc.edit',
        params: {
          id: order.data.id,
        },
      });

      if (
        order.data.attributes.is_using_stock &&
        !order.data.attributes.is_loan
      ) {
        this.loadPayment();
      }
    },
    async onValidate() {
      this.confirmValidateOrderVisible = true;
    },
    onValidateConfirmed() {
      this.confirmValidateOrderVisible = false;
      this.viewOrderModalVisible = true;
    },
    onOrderDetailUpdated(data) {
      this.order.shippingFee = data.shippingFee;
      this.order.plasticQty = data.plasticQty;

      this.loadProductBundles()
    },
    onChangeSelectedBundles(selectedBundes) {
      const copySelectedBundles = [...selectedBundes]

      copySelectedBundles.sort(([, selectedAtA], [, selectedAtB]) => selectedAtA - selectedAtB)

      this.selectedBundes = copySelectedBundles.map(([bundleId]) => bundleId)
    },
    onChangeChoosenBonuses(choosenBonuses) {
      this.choosenBonuses = {...choosenBonuses}
    },
    updateBalanceUsedBalance() {
      this.balanceUsed.forEach((balanceUsed, index) => {
        this.balanceUsed[index].balance =
          this.order.destinationOffice[balanceUsed.type === 'restock_balance' ? 'temp_restock_balance' : 'multiplied_deposit_balance'];
      });
    },
    onUpdateRestockBalance(balance) {
      this.order.destinationOffice.temp_restock_balance = balance
    }
  },
  created() {
    this.loadSetup()
    
    if (this.$route.params.id) {
      this.loadOrder();
    } else {
      this.loadBuyerType();
    }
  },
};
</script>
